import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState, useRef, useCallback } from "react";
import client from "../api/client";

interface NetworkNode {
  id: number;
  x: number;
  y: number;
  originX: number;
  originY: number;
  vx: number;
  vy: number;
  r: number;
}

interface ScoreboardStats {
  participants: number;
  flagsSolved: number;
  challenges: number;
}

interface Category {
  name: string;
  count: number;
}

function useCountUp(target: number, duration: number = 1800) {
  const [value, setValue] = useState(0);
  const ref = useRef<number | null>(null);

  useEffect(() => {
    if (target === 0) return;
    const start = performance.now();
    const from = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        ref.current = requestAnimationFrame(tick);
      }
    };

    ref.current = requestAnimationFrame(tick);
    return () => {
      if (ref.current !== null) cancelAnimationFrame(ref.current);
    };
  }, [target, duration]);

  return value;
}

function StatNumber({ target, label }: { target: number; label: string }) {
  const count = useCountUp(target);
  return (
    <span className="flex items-center gap-2">
      <span className="font-mono text-lg text-ds-text font-semibold tabular-nums">
        {count.toLocaleString()}
      </span>
      <span className="text-ds-text-dim text-sm">{label}</span>
    </span>
  );
}

function NetworkGraph() {
  const svgRef = useRef<SVGSVGElement>(null);
  const nodesRef = useRef<NetworkNode[]>([]);
  const mouseRef = useRef<{ x: number; y: number }>({ x: -1000, y: -1000 });
  const circlesRef = useRef<SVGCircleElement[]>([]);
  const linesRef = useRef<SVGLineElement[]>([]);
  const rafRef = useRef<number>(0);
  const initializedRef = useRef(false);

  const CONNECTION_DIST = 180;
  const MOUSE_INFLUENCE = 200;
  const PULL_STRENGTH = 0.02;
  const DAMPING = 0.92;

  const initNodes = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const count = 28;
    const nodes: NetworkNode[] = [];
    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      nodes.push({
        id: i,
        x,
        y,
        originX: x,
        originY: y,
        vx: 0,
        vy: 0,
        r: 1.5 + Math.random() * 1.5,
      });
    }
    nodesRef.current = nodes;
  }, []);

  const animate = useCallback(() => {
    const nodes = nodesRef.current;
    const lines = linesRef.current;
    const mx = mouseRef.current.x;
    const my = mouseRef.current.y;

    for (const node of nodes) {
      const dx = mx - node.x;
      const dy = my - node.y;
      const distToMouse = Math.sqrt(dx * dx + dy * dy);

      if (distToMouse < MOUSE_INFLUENCE && distToMouse > 0) {
        const force =
          ((MOUSE_INFLUENCE - distToMouse) / MOUSE_INFLUENCE) * PULL_STRENGTH;
        node.vx += dx * force;
        node.vy += dy * force;
      }

      const toOriginX = node.originX - node.x;
      const toOriginY = node.originY - node.y;
      node.vx += toOriginX * 0.005;
      node.vy += toOriginY * 0.005;

      node.vx *= DAMPING;
      node.vy *= DAMPING;
      node.x += node.vx;
      node.y += node.vy;
    }

    let lineIdx = 0;
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const line = lines[lineIdx];
        if (dist < CONNECTION_DIST) {
          const opacity = (1 - dist / CONNECTION_DIST) * 0.18;
          line.setAttribute("x1", String(a.x));
          line.setAttribute("y1", String(a.y));
          line.setAttribute("x2", String(b.x));
          line.setAttribute("y2", String(b.y));
          line.setAttribute("opacity", String(opacity));
        } else {
          line.setAttribute("opacity", "0");
        }
        lineIdx++;
      }
    }

    const circles = circlesRef.current;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const dx = mx - node.x;
      const dy = my - node.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const glow = dist < MOUSE_INFLUENCE ? 1 - dist / MOUSE_INFLUENCE : 0;
      const opacity = 0.3 + glow * 0.7;
      const r = node.r + glow * 2;

      circles[i]?.setAttribute("cx", String(node.x));
      circles[i]?.setAttribute("cy", String(node.y));
      circles[i]?.setAttribute("r", String(r));
      circles[i]?.setAttribute("opacity", String(opacity));
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    initNodes();

    const w = window.innerWidth;
    const h = window.innerHeight;
    const svg = svgRef.current;
    if (!svg) return;

    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);

    const maxPairs =
      (nodesRef.current.length * (nodesRef.current.length - 1)) / 2;
    linesRef.current = [];
    for (let i = 0; i < maxPairs; i++) {
      const line = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      line.setAttribute("stroke", "#5b7cff");
      line.setAttribute("stroke-width", "0.5");
      line.setAttribute("opacity", "0");
      svg.appendChild(line);
      linesRef.current.push(line);
    }

    circlesRef.current = [];
    for (const node of nodesRef.current) {
      const circle = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "circle"
      );
      circle.setAttribute("cx", String(node.x));
      circle.setAttribute("cy", String(node.y));
      circle.setAttribute("r", String(node.r));
      circle.setAttribute("fill", "#5b7cff");
      circle.setAttribute("opacity", "0.3");
      svg.appendChild(circle);
      circlesRef.current.push(circle);
    }

    initializedRef.current = true;
    rafRef.current = requestAnimationFrame(animate);

    const handleMouse = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    const handleResize = () => {
      const nw = window.innerWidth;
      const nh = window.innerHeight;
      svg.setAttribute("viewBox", `0 0 ${nw} ${nh}`);
    };

    window.addEventListener("mousemove", handleMouse);
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", handleMouse);
      window.removeEventListener("resize", handleResize);
    };
  }, [initNodes, animate]);

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
}

const CATEGORY_ICONS: Record<string, string> = {
  Web: "\u{1F310}",
  Crypto: "\u{1F510}",
  Forensics: "\u{1F50D}",
  Pwn: "\u{1F480}",
  Reversing: "\u{2699}\u{FE0F}",
  Misc: "\u{1F4E6}",
  OSINT: "\u{1F575}\u{FE0F}",
  Hardware: "\u{1F527}",
  Blockchain: "\u{26D3}\u{FE0F}",
  Stego: "\u{1F5BC}\u{FE0F}",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

export default function LandingPage() {
  const [stats, setStats] = useState<ScoreboardStats>({
    participants: 0,
    flagsSolved: 0,
    challenges: 0,
  });
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    client
      .get("/scoreboard")
      .then((res) => {
        const d = res.data?.data ?? res.data;
        setStats({
          participants: d.participants ?? d.totalParticipants ?? 0,
          flagsSolved: d.flagsSolved ?? d.totalFlags ?? 0,
          challenges: d.challenges ?? d.totalChallenges ?? 0,
        });
      })
      .catch(() => {});

    client
      .get("/challenges/categories")
      .then((res) => {
        const d = res.data?.data ?? res.data;
        setCategories(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen ds-bg">
      {/* ─── HERO ─── */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <NetworkGraph />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-5xl md:text-7xl font-bold leading-tight"
          >
            <span className="bg-gradient-to-r from-white via-ds-accent to-ds-accent bg-clip-text text-transparent">
              CyberSentinel Academy
            </span>
          </motion.h1>

          <motion.p
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-6 text-ds-text-dim text-lg md:text-xl max-w-2xl mx-auto"
          >
            Competitive cybersecurity challenges. Real-time. Zero noise.
          </motion.p>

          <motion.p
            custom={1.5}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-3 text-ds-text-muted text-sm"
          >
            A product of CyberSec PRN 2026 FZCO
          </motion.p>

          <motion.div
            custom={3}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/challenges" className="ds-btn-primary px-8 py-3 text-base">
              Enter Arena
            </Link>
            <Link
              to="/scoreboard"
              className="ds-btn-ghost px-8 py-3 text-base border border-ds-border hover:border-ds-accent/50"
            >
              View Scoreboard
            </Link>
          </motion.div>

          <motion.div
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-16 flex items-center justify-center gap-6 text-sm"
          >
            <StatNumber target={stats.participants} label="participants" />
            <span className="text-ds-border">·</span>
            <StatNumber target={stats.flagsSolved} label="flags solved" />
            <span className="text-ds-border">·</span>
            <StatNumber target={stats.challenges} label="challenges" />
          </motion.div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-center text-ds-text mb-16"
        >
          Built for Competition
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Feature 1 — Dynamic Scoring */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: 0 }}
            className="ds-card p-8 group hover:shadow-[0_0_30px_rgba(91,124,255,0.08)] hover:border-ds-accent/30 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-lg bg-ds-accent/10 flex items-center justify-center mb-5">
              <svg
                viewBox="0 0 24 24"
                className="w-6 h-6 text-ds-accent"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="12" width="4" height="9" rx="1" />
                <rect x="10" y="7" width="4" height="14" rx="1" />
                <rect x="17" y="3" width="4" height="18" rx="1" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-ds-text mb-2">
              Dynamic Scoring
            </h3>
            <p className="text-ds-text-dim text-sm leading-relaxed">
              Points decrease as more participants solve a challenge. Early
              solvers earn the biggest rewards. Adapt fast or fall behind.
            </p>
          </motion.div>

          {/* Feature 2 — Real-time Leaderboard */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="ds-card p-8 group hover:shadow-[0_0_30px_rgba(91,124,255,0.08)] hover:border-ds-accent/30 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-lg bg-ds-accent/10 flex items-center justify-center mb-5">
              <svg
                viewBox="0 0 24 24"
                className="w-6 h-6 text-ds-accent"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 12c1.5-3 4.5-5 7-5s4.5 2 3 5-4.5 5-7 5-5.5-2-7-5z" />
                <path d="M14 12c1.5-3 4.5-5 7-5s4.5 2 3 5-4.5 5-7 5-5.5-2-7-5z" opacity="0.4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-ds-text mb-2">
              Real-time Leaderboard
            </h3>
            <p className="text-ds-text-dim text-sm leading-relaxed">
              Live score updates streamed via WebSocket. Watch positions shift
              in real time as teams race for the top.
            </p>
          </motion.div>

          {/* Feature 3 — Team Warfare */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="ds-card p-8 group hover:shadow-[0_0_30px_rgba(91,124,255,0.08)] hover:border-ds-accent/30 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-lg bg-ds-accent/10 flex items-center justify-center mb-5">
              <svg
                viewBox="0 0 24 24"
                className="w-6 h-6 text-ds-accent"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="6" cy="7" r="2.5" />
                <circle cx="18" cy="7" r="2.5" />
                <circle cx="12" cy="17" r="2.5" />
                <line x1="8.2" y1="8.7" x2="10" y2="15" />
                <line x1="15.8" y1="8.7" x2="14" y2="15" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-ds-text mb-2">
              Team Warfare
            </h3>
            <p className="text-ds-text-dim text-sm leading-relaxed">
              Form a team, split the workload, and dominate together. Shared
              scoreboards and role-based access for coordinated attacks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── CATEGORIES ─── */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-center text-ds-text mb-12"
        >
          Challenge Categories
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="overflow-x-auto pb-4 scrollbar-thin"
        >
          <div className="flex gap-4 w-max mx-auto justify-center">
            {(categories.length > 0
              ? categories
              : [
                  { name: "Web", count: 0 },
                  { name: "Crypto", count: 0 },
                  { name: "Forensics", count: 0 },
                  { name: "Pwn", count: 0 },
                  { name: "Reversing", count: 0 },
                  { name: "Misc", count: 0 },
                  { name: "OSINT", count: 0 },
                  { name: "Hardware", count: 0 },
                  { name: "Blockchain", count: 0 },
                  { name: "Stego", count: 0 },
                ]
            ).map((cat) => (
              <Link
                key={cat.name}
                to="/challenges"
                className="ds-card px-6 py-4 flex items-center gap-3 hover:border-ds-accent/40 hover:shadow-[0_0_20px_rgba(91,124,255,0.06)] transition-all duration-200 whitespace-nowrap"
              >
                <span className="text-xl" role="img" aria-label={cat.name}>
                  {CATEGORY_ICONS[cat.name] ?? "\u{2B55}"}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-ds-text">
                    {cat.name}
                  </span>
                  <span className="text-xs text-ds-text-muted font-mono">
                    {cat.count > 0 ? `${cat.count} challenges` : "loading..."}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative z-10 overflow-hidden py-32">
        {/* Radar pulse rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="radar-ring"
              style={{
                width: `${200 + i * 120}px`,
                height: `${200 + i * 120}px`,
                animationDelay: `${i * 0.6}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-bold text-ds-text mb-8"
          >
            Ready to test your skills?
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Link
              to="/register"
              className="ds-btn-primary px-10 py-3.5 text-base font-semibold"
            >
              Join Now
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
